import type { CustomizationDraft, PiclickWP } from '@/types/piclick';
import { LETTER_SYMBOL_ID } from '@/lib/customization';

type Props = {
  draft: CustomizationDraft;
  options: PiclickWP;
  onKeyClick?: (index: number) => void;
  compact?: boolean;
};

export function Visualizer({ draft, options, onKeyClick, compact = false }: Props) {
  const baseColor = options.baseColors.find((c) => c.id === draft.baseColorId);
  const accessory = options.accessories.find((a) => a.id === draft.accessoryId);
  const size = options.sizes.find((s) => s.id === draft.sizeId);

  const baseHex = baseColor?.hex ?? '#fffdf5';
  const hasBase = !!baseColor;
  const slots = draft.keys.length;

  if (slots === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 rounded-[2rem] border-2 border-dashed border-plum/40 bg-gradient-to-b from-pink/5 to-cream/60 px-8 py-12 text-center">
        <span className="inline-block rounded-full border-[3px] border-plum bg-butter px-4 py-1.5 font-action text-[0.8rem] font-bold uppercase tracking-wider text-plum shadow-[0_3px_0_0_#600b2e]">
          Seu Piclick
        </span>
        <div
          aria-hidden="true"
          className="flex items-center gap-2 opacity-40"
        >
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-11 w-11 rounded-[0.9rem] border-2 border-dashed border-plum/50 bg-white/40 sm:h-14 sm:w-14 sm:rounded-[1.1rem]"
            />
          ))}
        </div>
        <span className="max-w-[18rem] font-action text-sm text-plum/70">
          Escolha um tamanho pra começar a montar o seu clique perfeito ✨
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-3 sm:gap-5">
      <span className="rounded-full border-[3px] border-plum bg-butter px-4 py-1.5 font-action text-[0.8rem] font-bold uppercase tracking-wider text-plum shadow-[0_3px_0_0_#600b2e]">
        Seu Piclick
      </span>

      <div className="flex items-center gap-1 sm:gap-2">
        <div
          className="flex items-center gap-2 rounded-[1.75rem] border-[3px] border-plum p-2.5 sm:gap-3 sm:rounded-[2rem] sm:p-3.5"
          style={{
            backgroundColor: hasBase ? baseHex : 'rgba(255,253,245,0.6)',
            boxShadow: hasBase
              ? '0 10px 0 -2px #600b2e, inset 0 6px 10px rgba(255,255,255,0.55), 0 24px 36px rgba(96,11,46,0.18)'
              : '0 6px 0 -2px rgba(96,11,46,0.4), 0 18px 28px rgba(96,11,46,0.1)',
          }}
        >
          {draft.keys.map((k, i) => {
            const color = options.keyColors.find((c) => c.id === k.colorId);
            const symbol = options.symbols.find((s) => s.id === k.symbolId);
            const keyHex = color?.hex;
            const isFilled = !!color && !!symbol;
            return (
              <button
                key={i}
                type="button"
                onClick={() => onKeyClick?.(i)}
                aria-label={`Tecla ${i + 1}${
                  isFilled ? `: ${symbol?.label}, cor ${color?.label}` : ' (não configurada)'
                }`}
                className={`flex h-11 w-11 items-center justify-center rounded-[0.9rem] border-2 transition-transform active:translate-y-[1px] sm:h-14 sm:w-14 sm:rounded-[1.1rem] ${
                  isFilled
                    ? 'border-white/70'
                    : 'border-dashed border-plum/40 bg-white/40'
                }`}
                style={
                  isFilled
                    ? {
                        backgroundColor: keyHex,
                        boxShadow:
                          '0 3px 0 -1px rgba(96,11,46,0.55), inset 0 2px 4px rgba(255,255,255,0.7)',
                      }
                    : undefined
                }
              >
                {symbol?.id === LETTER_SYMBOL_ID && k.letterValue ? (
                  <span
                    className="font-action text-xl font-bold text-white sm:text-2xl"
                    style={{
                      textShadow:
                        '0 1px 0 rgba(0,0,0,0.28), 0 2px 3px rgba(0,0,0,0.18)',
                    }}
                  >
                    {k.letterValue}
                  </span>
                ) : symbol?.iconUrl ? (
                  <img
                    src={symbol.iconUrl}
                    alt=""
                    className="h-5 w-5 sm:h-7 sm:w-7"
                    style={{
                      filter:
                        'brightness(0) invert(1) drop-shadow(0 1px 0 rgba(0,0,0,0.28)) drop-shadow(0 2px 2px rgba(0,0,0,0.18))',
                    }}
                  />
                ) : (
                  <span className="text-[10px] font-bold text-plum/40 sm:text-xs">
                    {i + 1}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {!compact && slots > 0 && (
        <p className="font-action text-xs text-plum/70 sm:text-sm">
          Toque nas teclas! ☝️
        </p>
      )}

      {!compact && (size || baseColor || accessory) && (
        <p className="text-center text-xs text-plum/60">
          {[
            size && size.label,
            baseColor && baseColor.label,
            accessory && accessory.label,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      )}
    </div>
  );
}
